import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { FinancialSupportCaseResultDto } from '@hello-unicorn/api-client';
import { createFinancialSupportExperienceResult } from '../data/financial-support-experience.data';
import { FinancialSupportDraft } from '../models/financial-support.model';

@Injectable({ providedIn: 'root' })
export class FinancialSupportExperienceService {
  private readonly destroyRef = inject(DestroyRef);
  private progressTimer: ReturnType<typeof setTimeout> | null = null;

  readonly progressSteps = [
    '유사기업 데이터 검색',
    '현재 기업 런웨이 분석',
    '소상공인 정책자금 기준 조회',
    '기업유형 확인',
    '업종 확인',
    '업력 확인',
    '금융지원 목적 확인',
    '희망금액 확인',
    '적합한 금융지원 상품 분석',
  ] as const;
  readonly similarCompanySteps = [
    '유사기업 데이터 검색',
    '유사기업 기준 생성',
    '유사기업 금융행동 데이터 확인',
  ] as const;
  readonly policyFundSteps = [
    '기업유형 확인',
    '업종 확인',
    '업력 확인',
    '금융지원 목적 확인',
    '희망금액 확인',
    '현재 정책자금 기준 확인',
    '적합한 금융지원 상품 분석',
  ] as const;
  readonly activeSteps = signal<readonly string[]>([]);
  readonly result = signal<FinancialSupportCaseResultDto | null>(null);
  readonly currentStep = signal(-1);
  readonly completedStepCount = signal(0);
  readonly isCompleted = signal(false);
  readonly selectedPolicyFundRevisionId = signal<string | null>(null);
  readonly selectedAmount = signal<number | null>(null);

  constructor() {
    this.destroyRef.onDestroy(() => this.clearProgressTimer());
  }

  prepare(draft: FinancialSupportDraft) {
    const result = createFinancialSupportExperienceResult(draft);
    this.result.set(result);
    this.selectedPolicyFundRevisionId.set(result.matches[0]?.policyFundRevisionId ?? null);
    this.selectedAmount.set(result.case.requestedAmount);
    return result;
  }

  selectPolicyFund(policyFundRevisionId: string, selectedAmount: number) {
    this.selectedPolicyFundRevisionId.set(policyFundRevisionId);
    this.selectedAmount.set(selectedAmount);
  }

  async runAnalysisProgress(interval = Math.ceil(8400 / this.progressSteps.length)) {
    return this.runProgress(this.progressSteps, interval);
  }

  async runProgress(steps: readonly string[], interval = 500) {
    this.clearProgressTimer();
    this.activeSteps.set(steps);
    this.currentStep.set(0);
    this.completedStepCount.set(0);
    this.isCompleted.set(false);

    for (let index = 0; index < steps.length; index += 1) {
      this.currentStep.set(index);
      await this.wait(interval);
      this.completedStepCount.set(index + 1);
    }

    this.currentStep.set(-1);
    this.isCompleted.set(true);
  }

  private wait(interval: number) {
    return new Promise<void>(resolve => {
      this.progressTimer = setTimeout(() => {
        this.progressTimer = null;
        resolve();
      }, interval);
    });
  }

  private clearProgressTimer() {
    if (!this.progressTimer) return;
    clearTimeout(this.progressTimer);
    this.progressTimer = null;
  }
}
